/**
 * S3 — count entry. The screen managers actually live in.
 *
 * **Offline-tolerant since D4.** Entries land in IndexedDB first and reach BC
 * staging when there is a connection; the header always says how many are still
 * held here. Review and posting stay online-only and say so, which keeps the
 * offline surface small enough to reason about (§ 4.6).
 *
 * The rulings this screen exists to honour, each implemented somewhere specific:
 *
 * - **D-1, blind count.** No BC on-hand in the count list payload (it has never
 *   carried it) and none in the offline snapshot, which holds no money. Two
 *   amendments, both from separate server reads: Cold Link shows on-hand beside the
 *   box (2026-10-29), and the other five areas reveal it for ONE item once that
 *   item's line has saved (2026-10-30) — the server refuses before then.
 * - **Rule 5, blank is not zero.** Two visual channels per `ItemRow`; the only
 *   clear action is S4's, and a *cleared* item is storable offline as a draft of
 *   `null` — a real record, distinct from an item nobody touched.
 * - **One total per item** (2026-08-15), lot-tracked items included. No lot prompt; the lot
 *   marker is informational.
 * - **A 289-item list has to be usable.** Search over number and description,
 *   section jump, uncounted-only, sort, and per-group progress — plus, since
 *   Kevin's item 8, a vendor filter and a section-or-vendor grouping. Every one of
 *   those is additive and starts where the screen has always been.
 *
 * **Session open pulls staging first, then replays what is held on top** (§ 4.2):
 * the pull is `SessionRoute`'s read, the replay is the flush below, and what the
 * replay overwrote is reported rather than merged (§ 4.4).
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";

import { useCountList, usePendingReceipts } from "../api/queries";
import type { CountLine, CountListItem } from "../api/types";
import { defaultUnit, purchaseUnit } from "../entry/defaultUnit";
import { ItemRow } from "../entry/ItemRow";
import { ItemSheet } from "../entry/ItemSheet";
import { OnHandBar } from "../entry/OnHandBar";
import { PendingReceiptsPanel, pendingMarker } from "../entry/PendingReceiptsPanel";
import { RevealCell } from "../entry/RevealCell";
import { useEntryOnHand } from "../entry/useOnHand";
import {
  allSections,
  allVendors,
  blurOutcome,
  isQuantityText,
  progressWithDrafts,
  resolveEntry,
  sectionsWithDrafts,
  stagedByItem,
  UNVENDORED,
  type EntryFilters,
  type GroupBy,
  type SortOrder,
  type SyncState,
} from "../entry/entryModel";
import { pruneSyncedDrafts, recordPull, snapshotItems } from "../offline/draftStore";
import { BlockedFlushNotice, ReconciliationReport, SyncStatusLine } from "../offline/SyncPanel";
import { DEBOUNCE_MS, useDraftSync } from "../offline/useDraftSync";
import { isEditable } from "../sessions/rules";
import type { SessionScreenProps } from "./SessionRoute";

const CLEAR_REFUSED_NOTE =
  "Emptying the box does not clear a count. Open the item and use “Clear this count” " +
  "if you mean to leave its inventory unchanged — or type 0 to count it as none.";

export function Entry({ session, areaId, principal }: SessionScreenProps) {
  const list = useCountList(areaId, true);
  const sync = useDraftSync(areaId, session, principal);
  // Cold Link only; inert (never fetches) at every blind-count area.
  const bc = useEntryOnHand(list.data?.area, areaId, session.session_id);
  // Open PO/transfer documents (PHASE_pending-receipts-warning.md): a separate, live
  // read, never part of the count list or the drafts. Documents, not stock — D-1 holds.
  const pending = usePendingReceipts(areaId, session.session_id, sync.online);

  const [typed, setTyped] = useState<Record<string, string>>({});
  const [messages, setMessages] = useState<Record<string, string>>({});
  const [filters, setFilters] = useState<EntryFilters>({
    search: "",
    section: "",
    uncountedOnly: false,
    sort: "list",
    // Item 8's two controls both start where the screen has always been: every
    // vendor, grouped by section. Wholesale counts by item number and must be
    // unaffected until somebody chooses otherwise.
    vendor: "",
    groupBy: "section",
  });
  const [sheetItemNo, setSheetItemNo] = useState<string | null>(null);
  const timers = useRef(new Map<string, ReturnType<typeof setTimeout>>());
  const replayed = useRef(false);

  const items = list.data?.items ?? [];
  const staged = useMemo(() => stagedByItem(session), [session]);
  const editable = isEditable(session);
  const drafts = sync.drafts;

  // The offline snapshot, taken while online — descriptions, UOMs, sections and the
  // lot marker, and no money (§ 4.1). Written alongside the pull so both
  // timestamps describe the same moment.
  useEffect(() => {
    if (!list.data) return;
    const snapshot = list.data;
    void (async () => {
      await recordPull(session, areaId);
      await snapshotItems(session.session_id, snapshot);
    })();
  }, [areaId, list.data, session]);

  // Replay once per mount, after the pull: anything held from a previous visit goes
  // up now. Guarded because two flushes in flight would report the same overwrites
  // twice — the flush itself is idempotent, the reporting is not.
  useEffect(() => {
    if (replayed.current || !list.data) return;
    replayed.current = true;
    // Prune first, then replay: pruning is a session-open act (see
    // `pruneSyncedDrafts`), and doing it here rather than inside `recordPull` keeps
    // it from erasing the "saved" chip on every subsequent sync.
    void (async () => {
      await pruneSyncedDrafts(session);
      await sync.flush();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [list.data]);

  useEffect(() => {
    const pending = timers.current;
    return () => {
      for (const timer of pending.values()) clearTimeout(timer);
      pending.clear();
    };
  }, []);

  const progress = useMemo(
    () => progressWithDrafts(items, staged, drafts),
    [items, staged, drafts],
  );
  const sections = useMemo(
    () => sectionsWithDrafts(items, staged, drafts, filters),
    [items, staged, drafts, filters],
  );
  const sectionOptions = useMemo(() => allSections(items), [items]);
  const vendorOptions = useMemo(() => allVendors(items), [items]);
  // A list where BC holds no vendor for a single item has nothing to filter by,
  // and an empty dropdown reads like a broken control rather than an empty fact.
  const hasVendors = vendorOptions.some((v) => v.key !== UNVENDORED);

  function setOne<T>(
    setter: (fn: (prev: Record<string, T>) => Record<string, T>) => void,
    key: string,
    value: T | undefined,
  ) {
    setter((prev) => {
      const next = { ...prev };
      if (value === undefined) delete next[key];
      else next[key] = value;
      return next;
    });
  }

  /** What this row's quantity currently is, draft over staging. */
  function resolvedLine(item: CountListItem): CountLine | undefined {
    const upper = item.item_no.trim().toUpperCase();
    const resolved = resolveEntry(staged.get(upper)?.counted_qty, drafts.get(upper));
    if (resolved.qty === null) return undefined;
    const stagedLine = staged.get(upper);
    return {
      line_no: stagedLine?.line_no ?? 0,
      item_no: upper,
      unit_of_measure_code: stagedLine?.unit_of_measure_code ?? defaultUnit(item),
      counted_qty: resolved.qty,
      entered_by: stagedLine?.entered_by ?? null,
      entered_at: stagedLine?.entered_at ?? null,
      lots: stagedLine?.lots ?? [],
    };
  }

  /**
   * Typing: hold it locally and arm the debounce.
   *
   * The debounce deliberately does **not** apply the blur rules. Mid-typing the box
   * is transiently empty — a manager deleting `12` to type `120` — and treating
   * that as a clear-refusal would fight them keystroke by keystroke. An empty or
   * half-typed box simply waits; blur is where the rules apply.
   */
  function onChange(item: CountListItem, text: string) {
    const key = item.item_no;
    setOne(setTyped, key, text);
    setOne(setMessages, key, undefined);

    const existing = timers.current.get(key);
    if (existing) clearTimeout(existing);

    const trimmed = text.trim();
    if (trimmed === "" || !isQuantityText(trimmed)) return;

    timers.current.set(
      key,
      setTimeout(() => {
        timers.current.delete(key);
        const current = resolvedLine(item);
        if (current && current.counted_qty.trim() === trimmed) return;
        void sync.record(key, trimmed, recordedUnit(item));
        setOne(setTyped, key, undefined);
      }, DEBOUNCE_MS),
    );
  }

  /**
   * The unit this row is counted in (item 3).
   *
   * A draft's choice wins, then whatever is already staged, then the default:
   * the item's purchase unit where Business Central holds one that differs from
   * base, else base (RULED 2026-09-24, `PHASE_purchase-uom-default.md`, which
   * reversed the 2026-08-31 base default on exactly this last fallback). A line
   * already counted keeps its unit through the first two steps — that
   * precedence is older than this default and is not what changed.
   */
  function unitFor(item: CountListItem): string | null {
    const key = item.item_no;
    return (
      drafts.get(key)?.unit_of_measure_code ??
      staged.get(key.trim().toUpperCase())?.unit_of_measure_code ??
      defaultUnit(item)
    );
  }

  /**
   * The unit actually RECORDED against this row, or null if nothing need be sent.
   *
   * Same precedence as `unitFor`, with a different last step, and that step is
   * the one with posting consequences (`PHASE_purchase-uom-default.md` § 2):
   *
   * - On a purchase-differs item a still-blank line now sends the purchase code
   *   **explicitly**. "Send nothing" can no longer stand in for the default,
   *   because the staging table's own default is base — leaving the key off
   *   would show `BOX 20LB` on screen and post as if `OZ` had been chosen.
   * - On a purchase == base item this stays `null`, so that line's staging body
   *   is byte-identical to what this app sent before item 3: no
   *   `unit_of_measure_code` on the wire, and the staging table applies base.
   *
   * An explicit switch *back* to base is a real choice and is stored as such, so
   * it still overrides a previously staged unit.
   */
  function recordedUnit(item: CountListItem): string | null {
    const key = item.item_no;
    return (
      drafts.get(key)?.unit_of_measure_code ??
      staged.get(key.trim().toUpperCase())?.unit_of_measure_code ??
      purchaseUnit(item)
    );
  }

  /**
   * Changing the unit re-sends the row's current quantity under the new code.
   *
   * The number itself is never touched — 4 BAG is not 4 LB converted, it is the
   * manager saying they counted four bags. Business Central converts at posting.
   * A row with nothing counted yet records the choice and no quantity, so picking
   * a unit before typing survives a walk-in with no signal.
   */
  function onUnitChange(item: CountListItem, code: string) {
    const key = item.item_no;
    setOne(setMessages, key, undefined);
    const current = resolvedLine(item);
    void sync.record(key, current?.counted_qty ?? null, code);
  }

  /** Leaving the field: the full rules, including the clear refusal. */
  function onCommit(item: CountListItem) {
    const key = item.item_no;
    const text = typed[key];
    const timer = timers.current.get(key);
    if (timer) {
      clearTimeout(timer);
      timers.current.delete(key);
    }
    if (text === undefined) return;

    // Compared against the *resolved* value — a pending draft if there is one,
    // otherwise staging. Comparing against staging alone would re-send an
    // unlanded draft on every blur.
    const outcome = blurOutcome(text, resolvedLine(item));

    if (outcome.kind === "noop") {
      setOne(setTyped, key, undefined);
      return;
    }
    if (outcome.kind === "clear-refused") {
      setOne(setTyped, key, undefined);
      setOne(setMessages, key, CLEAR_REFUSED_NOTE);
      return;
    }
    if (outcome.kind === "invalid") {
      setOne(setMessages, key, outcome.reason);
      return;
    }
    setOne(setTyped, key, undefined);
    void sync.record(key, outcome.value, recordedUnit(item));
  }

  const sheetItem = sheetItemNo ? items.find((i) => i.item_no === sheetItemNo) : undefined;
  const heldDrafts = [...drafts.values()].filter((d) => d.sync_state !== "synced");

  if (list.isLoading) {
    return <p className="muted">Loading this area's count list…</p>;
  }

  if (list.error) {
    return (
      <section className="card error" data-testid="entry-screen">
        <h2>Could not load the count list</h2>
        <p>{(list.error as Error).message}</p>
        <p className="muted small">
          Nothing already entered is affected — counts live in Business Central's
          staging and in this device's draft store, not in this screen.
        </p>
        <p>
          <Link to={`/areas/${encodeURIComponent(areaId)}`}>← Back to this area's counts</Link>
        </p>
      </section>
    );
  }

  return (
    <section className="entry" data-testid="entry-screen">
      <header className="entry__header">
        <div>
          <h2>{session.area.display_name} — count</h2>
          <p className="muted small">
            Posting date {session.posting_date ?? "—"} · {session.area.location_code} ·{" "}
            {session.area.company_name}
          </p>
        </div>
        <div className="entry__progress" data-testid="progress">
          <strong>
            {progress.counted} of {progress.total}
          </strong>{" "}
          counted
          <div className="muted small">{progress.uncounted} still blank</div>
        </div>
      </header>

      <OnHandBar
        flagged={bc.flagged}
        asOf={bc.asOf}
        loading={bc.loading}
        error={bc.error}
        onRefresh={bc.refresh}
      />

      <PendingReceiptsPanel
        data={pending.data}
        loading={pending.isLoading || !sync.online}
        error={pending.error ? (pending.error as Error).message : null}
      />

      <SyncStatusLine
        online={sync.online}
        pending={sync.pending}
        refused={sync.refused}
        flushing={sync.flushing}
      />

      {!sync.online && (
        <div className="card notice small" data-testid="offline-notice">
          <strong>No connection — keep counting.</strong> Every entry is held on this
          iPad and goes to Business Central by itself when the signal comes back.
          Nothing is lost, and nothing is sent twice.
        </div>
      )}

      {sync.pending > 0 && sync.online && (
        <p className="small">
          <button
            type="button"
            className="ghost"
            onClick={() => void sync.flush()}
            disabled={sync.flushing}
            data-testid="sync-now"
          >
            {sync.flushing ? "Syncing…" : `Sync ${sync.pending} now`}
          </button>
        </p>
      )}

      {sync.flushWarnings.length > 0 && (
        <div className="card notice small" data-testid="flush-warnings">
          {/* Warnings a batch flush returned. They can name several items, so they
              sit here rather than on one row. */}
          {sync.flushWarnings.map((w) => (
            <p key={w}>{w}</p>
          ))}
        </div>
      )}

      <ReconciliationReport overwrites={sync.overwrites} onDismiss={sync.dismissOverwrites} />
      <BlockedFlushNotice block={sync.block} drafts={heldDrafts} onDiscard={sync.discard} />

      {list.data?.warnings?.length ? (
        <div className="card notice small" data-testid="list-warnings">
          {list.data.warnings.map((w) => (
            <p key={w}>{w}</p>
          ))}
        </div>
      ) : null}

      {!editable && (
        <div className="card error" data-testid="not-editable">
          <p>
            <strong>Staging has frozen this count at {session.status}.</strong> It can be
            read but not changed.
          </p>
        </div>
      )}

      <div className="entry__controls">
        <div className="entry__search">
          <label htmlFor="search">Search this list</label>
          <input
            id="search"
            type="search"
            inputMode="search"
            autoComplete="off"
            placeholder="Item number or description"
            value={filters.search}
            onChange={(event) => setFilters({ ...filters, search: event.target.value })}
            data-testid="search"
          />
        </div>
        <div className="entry__section">
          <label htmlFor="section">Section</label>
          <select
            id="section"
            value={filters.section}
            onChange={(event) => setFilters({ ...filters, section: event.target.value })}
            data-testid="section-jump"
          >
            <option value="">All sections</option>
            {sectionOptions.map((s) => (
              <option key={s.key} value={s.key}>
                {s.label}
              </option>
            ))}
          </select>
        </div>
        {/* Item 8. Both controls are additive: the section jump above and the sort
            below are untouched, and this pair starts at "every vendor, grouped by
            section", which is exactly the screen as it was. Hidden entirely when
            Business Central holds no vendor for anything on the list — including
            when the LSItem read was refused, in which case the warnings panel
            above already says so. */}
        {hasVendors && (
          <>
            <div className="entry__vendor">
              <label htmlFor="vendor">Vendor</label>
              <select
                id="vendor"
                value={filters.vendor}
                onChange={(event) => setFilters({ ...filters, vendor: event.target.value })}
                data-testid="vendor-filter"
              >
                <option value="">All vendors</option>
                {vendorOptions.map((v) => (
                  <option key={v.key} value={v.key}>
                    {v.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="entry__group-by">
              <label htmlFor="group-by">Group by</label>
              <select
                id="group-by"
                value={filters.groupBy}
                onChange={(event) =>
                  setFilters({ ...filters, groupBy: event.target.value as GroupBy })
                }
                data-testid="group-by"
              >
                <option value="section">Section</option>
                <option value="vendor">Vendor</option>
              </select>
            </div>
          </>
        )}
        <div className="entry__sort">
          <label htmlFor="sort">Sort</label>
          <select
            id="sort"
            value={filters.sort}
            onChange={(event) =>
              setFilters({ ...filters, sort: event.target.value as SortOrder })
            }
            data-testid="sort-order"
          >
            <option value="list">List order</option>
            <option value="item_no">Item number</option>
            <option value="name">Name (A-Z)</option>
          </select>
        </div>
        <label className="entry__toggle">
          <input
            type="checkbox"
            checked={filters.uncountedOnly}
            onChange={(event) => setFilters({ ...filters, uncountedOnly: event.target.checked })}
            data-testid="uncounted-only"
          />
          Uncounted only
        </label>
        {/* Persistent and reachable at any scroll position (Kevin's iPad feedback,
            item 7) — this bar is already `position: sticky` for the search box, so
            the review link rides along rather than needing a bar of its own. */}
        <Link
          to={`/areas/${encodeURIComponent(areaId)}/sessions/${encodeURIComponent(
            session.session_id,
          )}/review`}
          className="entry__review-jump"
          data-testid="go-to-review"
        >
          Go to review
        </Link>
      </div>

      {sections.length === 0 ? (
        <div className="card" data-testid="no-matches">
          <p>
            Nothing on this list matches those filters.
            {filters.uncountedOnly && progress.uncounted === 0 && (
              <> Every item has been counted.</>
            )}
          </p>
        </div>
      ) : (
        sections.map((section) => (
          <section key={section.key} className="entry__group" data-group-by={filters.groupBy}>
            {/* The test id keeps the `section-` prefix it has always had; under
                `Group by: vendor` the key is a vendor name instead, which is what
                `data-group-by` above is for. */}
            <h3 className="entry__group-heading" data-testid={`section-${section.key.trim()}`}>
              {section.label}{" "}
              <span className="muted small">
                {section.counted} of {section.items.length} counted
              </span>
            </h3>
            <ul className="item-list">
              {section.items.map((item) => {
                const upper = item.item_no.trim().toUpperCase();
                const draft = drafts.get(upper);
                const resolved = resolveEntry(staged.get(upper)?.counted_qty, draft);
                const syncState: SyncState = draft ? draft.sync_state : "clean";
                return (
                  <ItemRow
                    key={item.item_no}
                    item={item}
                    staged={resolvedLine(item)}
                    state={resolved.state}
                    draft={typed[item.item_no]}
                    sync={syncState}
                    message={
                      messages[item.item_no] ??
                      sync.warnings.get(upper) ??
                      draft?.refusal ??
                      undefined
                    }
                    editable={editable}
                    unit={unitFor(item)}
                    onHandView={bc.viewFor(item, unitFor(item), typed[item.item_no] ?? resolved.qty)}
                    pendingMarker={pendingMarker(pending.data?.lines ?? [], item.item_no)}
                    reveal={
                      bc.flagged ? undefined : (
                        <RevealCell
                          areaId={areaId}
                          sessionId={session.session_id}
                          item={item}
                          saved={staged.get(upper)}
                          queued={
                            draft?.sync_state === "pending" && draft.counted_qty !== null
                          }
                          onCorrect={() =>
                            document.getElementById(`qty-${item.item_no}`)?.focus()
                          }
                        />
                      )
                    }
                    onChange={(text) => onChange(item, text)}
                    onCommit={() => onCommit(item)}
                    onUnitChange={(code) => onUnitChange(item, code)}
                    onOpenSheet={() => setSheetItemNo(item.item_no)}
                  />
                );
              })}
            </ul>
          </section>
        ))
      )}

      <p className="entry__foot">
        <Link to={`/areas/${encodeURIComponent(areaId)}`}>← Back to this area's counts</Link>
      </p>

      {sheetItem && (
        <ItemSheet
          item={sheetItem}
          staged={resolvedLine(sheetItem)}
          editable={editable}
          busy={drafts.get(sheetItem.item_no.trim().toUpperCase())?.sync_state === "pending"}
          onClose={() => setSheetItemNo(null)}
          onClear={async () => {
            // The one place in the app that records a blank: a draft of `null`,
            // which flushes as `counted_qty: null` and deletes the staging line.
            await sync.record(sheetItem.item_no, null);
            setSheetItemNo(null);
          }}
        />
      )}
    </section>
  );
}
